import React from 'react';
import { FaTiktok, FaInstagram, FaXTwitter, FaLinkedinIn, FaFacebookF } from 'react-icons/fa6';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useSettings } from '../../context/SettingsContext';

const { FiMapPin, FiPhone, FiMail } = FiIcons;

const socials = [
  { key: 'social_tiktok_url',    icon: FaTiktok,    label: 'TikTok' },
  { key: 'social_instagram_url', icon: FaInstagram, label: 'Instagram' },
  { key: 'social_x_url',         icon: FaXTwitter,  label: 'X' },
  { key: 'social_linkedin_url',  icon: FaLinkedinIn, label: 'LinkedIn' },
  { key: 'social_facebook_url',  icon: FaFacebookF, label: 'Facebook' },
];

const SocialLinks = ({ settings }) => {
  const active = socials.filter(s => settings[s.key]);
  if (!active.length) return null;
  return (
    <div className="flex items-center gap-3">
      {active.map(({ key, icon: Icon, label }) => (
        <a
          key={key}
          href={settings[key]}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={label}
          className="w-9 h-9 rounded-full flex items-center justify-center bg-gray-800 hover:bg-gray-700 text-white transition-colors"
        >
          <Icon className="w-4 h-4" />
        </a>
      ))}
    </div>
  );
};

const brandName = (s) => s.app_name || s.footer_org_name || 'Vocaverse';

const BusinessFooter = ({ settings }) => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
    {/* Address Details */}
    <div>
      <h3 className="text-xl font-bold mb-6 flex items-center gap-3">
        <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center p-1">
          <img src={settings.app_logo_url || '/logo.png'} alt="Logo" className="w-full h-full object-contain" />
        </div>
        {brandName(settings)}
      </h3>
      <p className="text-gray-400 mb-6 max-w-sm">
        {settings.footer_tagline}
      </p>
      {settings.footer_address && (
        <div className="space-y-4">
          <div className="flex items-start gap-3 text-gray-300">
            <SafeIcon icon={FiMapPin} className="w-5 h-5 mt-1 flex-shrink-0" style={{ color: 'var(--brand-accent)' }} />
            <span>{settings.footer_address}</span>
          </div>
        </div>
      )}
    </div>

    {/* Contact Details */}
    <div>
      <h3 className="text-lg font-semibold mb-6">Contact Us</h3>
      <div className="space-y-4">
        {settings.footer_phone && (
          <a
            href={`tel:${(settings.footer_phone || '').replace(/\s/g, '')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 text-gray-300 hover:text-white transition-colors"
          >
            <SafeIcon icon={FiPhone} className="w-5 h-5" style={{ color: 'var(--brand-accent)' }} />
            {settings.footer_phone}
          </a>
        )}
        {settings.footer_email && (
          <a
            href={`mailto:${settings.footer_email}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 text-gray-300 hover:text-white transition-colors"
          >
            <SafeIcon icon={FiMail} className="w-5 h-5" style={{ color: 'var(--brand-accent)' }} />
            {settings.footer_email}
          </a>
        )}
      </div>
    </div>

    {/* Static Map */}
    {settings.footer_map_url && settings.footer_map_img && (
      <div>
        <h3 className="text-lg font-semibold mb-6">Find Us</h3>
        <a
          href={settings.footer_map_url}
          target="_blank"
          rel="noopener noreferrer"
          className="block rounded-xl overflow-hidden h-48 bg-gray-800 relative group cursor-pointer"
        >
          <img
            src={settings.footer_map_img}
            alt="Map location"
            className="w-full h-full object-cover opacity-70 group-hover:opacity-100 transition-opacity duration-300"
          />
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="p-2 rounded-full shadow-lg" style={{ backgroundColor: 'var(--brand-primary)' }}>
              <SafeIcon icon={FiMapPin} className="w-6 h-6 text-white" />
            </div>
          </div>
        </a>
      </div>
    )}
  </div>
);

const CustomFooter = ({ settings }) => {
  const lines = [
    { text: settings.footer_custom_line_1, show: settings.footer_custom_show_1 === 'true' },
    { text: settings.footer_custom_line_2, show: settings.footer_custom_show_2 === 'true' },
    { text: settings.footer_custom_line_3, show: settings.footer_custom_show_3 === 'true' },
    { text: settings.footer_custom_line_4, show: settings.footer_custom_show_4 === 'true' },
  ].filter(l => l.show && l.text);

  return (
    <div className="mb-12">
      {settings.footer_custom_title && (
        <h3 className="text-xl font-bold mb-6">{settings.footer_custom_title}</h3>
      )}
      {lines.length > 0 && (
        <div className="space-y-3 text-gray-300">
          {lines.map((l, i) => (
            <p key={i}>{l.text}</p>
          ))}
        </div>
      )}
    </div>
  );
};

const Footer = () => {
  const { settings } = useSettings();
  const isBusiness = settings.footer_layout !== 'custom';

  return (
    <footer className="pt-16 pb-8" style={{ backgroundColor: 'var(--brand-footer-bg, #111827)', color: 'var(--brand-footer-text, #ffffff)' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {isBusiness ? <BusinessFooter settings={settings} /> : <CustomFooter settings={settings} />}

        <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-6 text-sm text-gray-500">
          <p>&copy; {new Date().getFullYear()} {brandName(settings)}. All rights reserved.</p>
          <SocialLinks settings={settings} />
        </div>
      </div>
    </footer>
  );
};

export default Footer;
